import { useEffect, useRef, type ReactElement } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { dayStrip, shiftDay, type DayMode } from '@kratispot/shared';
import { dayMonthOf, weekdayOf } from '@/lib/format';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';

const HINT = { past: 'pastDay', live: null, future: 'planningDay' } as const;

// Ten days either side of the one in view: a fortnight out is a drag, not
// fourteen taps at the arrow. The row itself never grows — it scrolls. §20
export const STRIP_SPAN = 10;

// One row at every width: about a week is on screen and the rest slides
// sideways inside its own scroller, with the arrows and Today put beside it. §20
export function DayStrip({
  date,
  today,
  mode,
  earliest,
  onPick,
}: {
  date: string;
  today: string;
  mode: DayMode;
  earliest?: string;
  onPick: (date: string) => void;
}): ReactElement {
  const { t, language } = useTranslation();
  const hint = HINT[mode];
  const selected = useRef<HTMLButtonElement | null>(null);
  // The day in view is centred on every change, however it was chosen.
  useEffect(() => {
    selected.current?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [date]);

  // §12 gives the visitor no yesterday, so the strip offers none either.
  const days = dayStrip(date, today, STRIP_SPAN).filter(
    (day) => earliest === undefined || day.date >= earliest,
  );
  const stuck = earliest !== undefined && date <= earliest;

  return (
    <div className="flex shrink-0 flex-col items-center gap-1 pt-3 px-safe">
      <div className="flex w-full max-w-xl items-center gap-1.5 px-2">
        <button
          type="button"
          aria-label={t('prevDay')}
          disabled={stuck}
          onClick={() => onPick(shiftDay(date, -1))}
          className="grid size-9 shrink-0 place-items-center rounded-lg text-primary hover:bg-secondary disabled:opacity-35"
        >
          <ChevronLeft className="size-5" aria-hidden />
        </button>
        <div
          className="flex min-w-0 flex-1 snap-x items-center gap-1.5 overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-testid="day-scroller"
        >
          {days.map((day) => (
            <button
              key={day.date}
              type="button"
              ref={day.date === date ? selected : undefined}
              aria-current={day.date === date ? 'date' : undefined}
              onClick={() => onPick(day.date)}
              className={cn(
                'shrink-0 snap-center rounded-xl border px-3 py-1 text-center text-xs leading-tight font-bold',
                day.date === date
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'bg-card text-muted-foreground hover:bg-secondary',
                day.mode === 'past' && day.date !== date && 'opacity-55',
              )}
            >
              {day.date === today ? t('today') : weekdayOf(day.date, language)}
              <small className="block text-[10px] font-semibold opacity-80">
                {dayMonthOf(day.date, language)}
              </small>
            </button>
          ))}
        </div>
        <button
          type="button"
          aria-label={t('nextDay')}
          onClick={() => onPick(shiftDay(date, 1))}
          className="grid size-9 shrink-0 place-items-center rounded-lg text-primary hover:bg-secondary"
        >
          <ChevronRight className="size-5" aria-hidden />
        </button>
        {date !== today && (
          <button
            type="button"
            onClick={() => onPick(today)}
            className="shrink-0 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-bold text-primary"
          >
            {t('today')}
          </button>
        )}
      </div>
      {/* The line keeps its height on every day, so nothing below it jumps. §20 */}
      <p className="h-4 text-[11px] font-semibold text-muted-foreground">
        {hint === null ? '' : t(hint)}
      </p>
    </div>
  );
}
